import React from "react";
import TextField from "@mui/material/TextField";
import { useField } from "formik";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBox from "@mui/icons-material/CheckBox";
import Switch from "@mui/material/Switch";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import { useStore } from "../../../Content/Dashboard/Calendar/EditEvent/store";
import { useFirstRender } from "../../../../constants";

import PropTypes from "prop-types";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const FormikText = ({ name, ...otherProps }, ref) => {
  const [field, meta] = useField(name);
  const configTextField = {
    ...field,
    ...otherProps,
    fullwidth: "true",
  };
  if (meta && meta.touched && meta.error) {
    configTextField.error = true;
    configTextField.helperText = meta.error;
  }
  return <TextField {...configTextField} />;
};

FormikText.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["standard", "filled", "outlined"]),
};
export { FormikText };

export const FormikTextMultiline = ({ name, ...otherProps }) => {
  const [field, meta] = useField(name);

  const configTextField = {
    ...field,
    ...otherProps,
    fullwidth: "true",
  };
  if (meta && meta.touched && meta.error) {
    configTextField.error = true;
    configTextField.helperText = meta.error;
  }
  return <TextField multiline minRows={5} maxRows={7} {...configTextField} />;
};

export const FormikCheckbox = ({ name, ...otherProps }) => {
  const [field, meta] = useField(name);

  const configTextField = {
    ...field,
    ...otherProps,
  };
  if (meta && meta.touched && meta.error) {
    configTextField.error = true;
    configTextField.helperText = meta.error;
  }
  return <CheckBox {...configTextField} />;
};

export const FormikSwitch = ({ name, ...otherProps }) => {
  const [field, meta] = useField(name);

  const configTextField = {
    ...field,
    ...otherProps,
  };
  if (meta && meta.touched && meta.error) {
    configTextField.error = true;
    configTextField.helperText = meta.error;
  }
  return <Switch size="small" {...configTextField} />;
};
export const FormikSelect = ({ name, children, ...otherProps }) => {
  const [field, meta] = useField(name);
  const configTextField = {
    ...field,
    ...otherProps,
  };
  // const { values } = otherProps
  if (meta && meta.touched && meta.error) {
    configTextField.error = true;
    configTextField.helperText = meta.error;
  }
  // const setValues = useStore(state => state.setValues)

  // React.useEffect(() => {
  //     if (values) {
  //         setValues(values)
  //     }
  // }, [values, setValues])

  return (
    <TextField {...configTextField} select>
      {children}
    </TextField>
  );
};

export const FormikAutoComplete2 = ({ name, ...otherProps }) => {
  const [field, meta] = useField(name);

  const {
    setFieldValue,
    label,
    multiple,
    placeholder,
    variant,
    defaultValue,
    options,
    values,
    disabled,
  } = otherProps;
  const configAutoCompleteField = {
    ...field,
  };
  if (meta && meta.touched && meta.error) {
    configAutoCompleteField.error = true;
    configAutoCompleteField.helperText = meta.error;
  }
  const valueRef = React.useRef(defaultValue);
  const group = React.useRef(values.group);
  const firstRender = useFirstRender();

  React.useEffect(() => {
    if (group.current !== values?.group && !firstRender && name !== "group") {
      valueRef.current = null;
      group.current = values.group;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.group]);
  const setValues = useStore((state) => state.setValues);

  React.useEffect(() => {
    if (values && name === "group") {
      setValues(values);
    }
  }, [values, setValues, name]);

  return (
    <Autocomplete
      multiple={multiple}
      disabled={disabled}
      limitTags={2}
      id={name}
      disablePortal
      ListboxProps={{ style: { maxHeight: "30vh" } }}
      options={Array.isArray(options) ? options : []}
      disableCloseOnSelect={multiple}
      autoHighlight
      getOptionLabel={(option) => option?.name}
      value={valueRef.current}
      isOptionEqualToValue={(option, value) => {
        return option?.id === value.id;
      }}
      onChange={(_, newValue) => {
        valueRef.current = newValue;
        if (newValue && newValue.id) {
          setFieldValue(name, newValue.id);
        } else {
          setFieldValue(name, null);
        }
      }}
      renderOption={(props, option, { selected, inputValue }) => {
        const matches = match(option.name, inputValue, { insideWords: true });
        const parts = parse(option.name, matches);

        return (
          <li {...props}>
            <div>
              {multiple && (
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{
                    fontWeight: part.highlight ? 700 : 400,
                    color: part.highlight ? "brown" : "inherit",
                  }}
                >
                  {part.text}
                </span>
              ))}
            </div>
          </li>
        );
      }}
      style={{ width: "100%" }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          name={name}
          label={label}
          {...configAutoCompleteField}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export function FormikRadioGroup({ name, children, ...otherProps }) {
  const { setFieldValue, defaultValue, disabled } = otherProps;
  const valueRef = React.useRef(defaultValue);

  const handleChange = (event) => {
    valueRef.current = event.target.value;
    setFieldValue(name, event.target.value);
  };
  return (
    <FormControl disabled={disabled}>
      <RadioGroup
        row
        aria-labelledby="meeting_type"
        name="type"
        value={valueRef.current}
        onChange={handleChange}
      >
        {children}
      </RadioGroup>
    </FormControl>
  );
}

export const FormikAutoComplete = ({ name, ...otherProps }) => {
  const [field, meta] = useField(name);
  const {
    setFieldValue,
    label,
    multiple,
    placeholder,
    variant,
    defaultValue,
    options,
    values,
    groupUsers,
    disabled,
  } = otherProps;
  const configAutoCompleteField = {
    ...field,
  };
  if (meta && meta.touched && meta.error) {
    configAutoCompleteField.error = true;
    configAutoCompleteField.helperText = meta.error;
  }
  const valueRef = React.useRef([]);

  const firstRender = useFirstRender();

  React.useEffect(() => {
    valueRef.current = defaultValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (
      !firstRender &&
      name !== "group" &&
      values &&
      Array.isArray(groupUsers[values.group])
    ) {
      valueRef.current = groupUsers[values.group].map((x) => x.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.group]);

  return (
    <Autocomplete
      multiple={multiple}
      limitTags={2}
      id={name}
      disablePortal
      disabled={disabled}
      ListboxProps={{ style: { maxHeight: "20vh" } }}
      options={Array.isArray(options) ? options : []}
      disableCloseOnSelect={multiple}
      autoHighlight
      getOptionLabel={(option) => {
        if (typeof option === "number") {
          return options.find((item) => item.id === option)?.name;
        } else {
          return option.name;
        }
      }}
      value={valueRef.current}
      isOptionEqualToValue={(option, value) => {
        if (Array.isArray(value)) {
          return value.includes(option.id);
        } else {
          return option.id === value;
        }
      }}
      renderOption={(props, option, { selected, inputValue }) => {
        const matches = match(option.name, inputValue, { insideWords: true });
        const parts = parse(option.name, matches);

        return (
          <li {...props}>
            <div>
              {multiple && (
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{
                    fontWeight: part.highlight ? 700 : 400,
                    color: part.highlight ? "#E1C699" : "inherit",
                  }}
                >
                  {part.text}
                </span>
              ))}
            </div>
          </li>
        );
      }}
      onChange={(_, value) => {
        valueRef.current = value.map((option) => option.id || option);
        setFieldValue(
          name,
          value !== null
            ? Array.isArray(value) && multiple
              ? valueRef.current
              : value.id
              ? value.id
              : defaultValue
            : ""
        );
      }}
      style={{ width: "100%" }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          name={name}
          label={label}
          {...configAutoCompleteField}
          placeholder={placeholder}
        />
      )}
    />
  );
};
